// client/src/dnd/emitter_tool/engine/engine_parts/emitter/shapeMath.js
// Small math helpers used by the spawn sampler and engine.

export const TAU = Math.PI * 2;
export const DEG2RAD = Math.PI / 180;

export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const lerp = (a, b, t) => a + (b - a) * t;
export const rand = (min, max) => min + Math.random() * (max - min);

export function normalize(x, y) {
  const m = Math.hypot(x, y);
  if (!m || !Number.isFinite(m)) return { x: 0, y: 0 };
  return { x: x / m, y: y / m };
}

export function rotate(x, y, deg) {
  if (!deg) return { x, y };
  const a = deg * DEG2RAD;
  const c = Math.cos(a), s = Math.sin(a);
  return { x: x * c - y * s, y: x * s + y * c };
}

/** CCW perpendicular (x, y) -> (-y, x) */
export function tangentCCW(x, y) {
  return { x: -y, y: x };
}

/** Angle of vector (x,y) in radians */
export function angleOf(x, y) {
  return Math.atan2(y, x);
}

/** Pick random point uniformly in a circle of radius R (center at origin) */
export function sampleInCircle(R) {
  const t = TAU * Math.random();
  const r = R * Math.sqrt(Math.random()); // sqrt for uniform area
  return { x: r * Math.cos(t), y: r * Math.sin(t), angle: t };
}

/** Random point on circumference of radius R */
export function sampleOnCircle(R) {
  const t = TAU * Math.random();
  return { x: R * Math.cos(t), y: R * Math.sin(t), angle: t };
}

/** Random point uniformly inside a sector [a0, a1] (radians) within radius R */
export function sampleInSector(a0, a1, R) {
  const t = a0 + (a1 - a0) * Math.random();
  const r = R * Math.sqrt(Math.random());
  return { x: r * Math.cos(t), y: r * Math.sin(t), angle: t };
}

/** Random point on circular arc between degrees [deg0, deg1] at radius R */
export function sampleOnArcDeg(deg0, deg1, R) {
  const t = (deg0 + (deg1 - deg0) * Math.random()) * DEG2RAD;
  return { x: R * Math.cos(t), y: R * Math.sin(t), angle: t };
}

/** Random point inside arc sector degrees [deg0, deg1] at radius R */
export function sampleInArcDeg(deg0, deg1, R) {
  const a0 = deg0 * DEG2RAD;
  const a1 = deg1 * DEG2RAD;
  return sampleInSector(a0, a1, R);
}
